import { FC, useState } from 'react';
import { Control, Controller, FieldError, FieldErrors, useWatch } from 'react-hook-form';
import { StanButton, StanTextArea } from '@/components';
import type { AutomationFormData } from '../../validations/automationForm';
import { cn, createIconElement } from '@/lib';
import { LinkButtonModal } from '../modals/LinkButtonModal';
import { useUser } from '@/queries';
import IcPencil from '@/assets/icons/ic-pencil.svg';
import IcPlus from '@/assets/icons/ic-plus.svg';

interface AutoDMFieldProps {
    control: Control<AutomationFormData>;
    error?: FieldError;
    buttonsError?: FieldErrors;
}

const MAX_CHARS = 1000;

export const AutoDMField: FC<AutoDMFieldProps> = ({ control, error, buttonsError }) => {
    const { user, isUserLoading } = useUser();
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

    // Check if user has auto_dm_buttons experiment enabled
    const hasAutoDMButtons = !isUserLoading && user?.experiments?.auto_dm_buttons === 1;

    const buttons = useWatch({ control, name: 'buttons' }) || [];
    const linkButton = buttons.length > 0 ? buttons[0] : null;
    const hasLinkButton = !!linkButton;

    // Extract button title error if it exists (buttons is an array, so check index 0)
    const buttonError = buttonsError?.[0] as any;
    const buttonTitleError = buttonError?.title?.message as string | undefined;

    return (
        <>
            <Controller
                name='content'
                control={control}
                render={({ field }) => {
                    const charCount = field.value?.length || 0;
                    const isOverLimit = charCount > MAX_CHARS;

                    return (
                        <div className='space-y-2 md:pt-3'>
                            <div className='space-y-1'>
                                <label htmlFor='content' className='text-label-xs-bold md:text-label-sm-bold block'>
                                    Auto-DM Message *
                                </label>
                                <p className='text-label-xs-regular text-gray md:text-body-sm max-md:hidden'>
                                    Sent via IG DM when someone triggers this automation. Add a Stan Store link and
                                    we&apos;ll track analytics for you!
                                </p>
                            </div>

                            <div>
                                {hasAutoDMButtons ? (
                                    <div
                                        className={cn(
                                            'group relative w-full rounded-md border pb-12 transition-all duration-200 md:pb-14',
                                            'border-disabled bg-canvas',
                                            'hover:border-brand-main',
                                            'focus-within:border-brand-main focus-within:shadow-material-focus-glow-default',
                                            (error || isOverLimit) && 'hover:border-negative-bold',
                                            (error || isOverLimit) &&
                                                'focus-within:border-negative-intense focus-within:shadow-material-focus-glow-error'
                                        )}
                                    >
                                        <textarea
                                            id='content'
                                            {...field}
                                            placeholder="Hey! Thanks for reaching out. Here's what you asked for..."
                                            rows={4}
                                            maxLength={MAX_CHARS}
                                            className={cn(
                                                'text-dark placeholder:text-neutral-fg-disabled w-full resize-none rounded-md border-none bg-transparent p-4 outline-none',
                                                'text-body-md min-h-24'
                                            )}
                                        />

                                        {/* Link Button */}
                                        <div className='absolute bottom-2.5 left-2.5 z-10'>
                                            {hasLinkButton ? (
                                                <div className='flex items-center gap-2'>
                                                    <StanButton
                                                        name='automation-edit-link-button'
                                                        size='xs'
                                                        color='secondary'
                                                        onClick={() => setIsLinkModalOpen(true)}
                                                        className='!bg-brand-subtle border-none'
                                                    >
                                                        {linkButton.title}
                                                    </StanButton>
                                                    <button
                                                        type='button'
                                                        onClick={() => setIsLinkModalOpen(true)}
                                                        className='text-gray hover:text-dark transition-colors'
                                                        aria-label='Edit link button'
                                                    >
                                                        {createIconElement(IcPencil, { className: 'size-4.5' })}
                                                    </button>
                                                </div>
                                            ) : (
                                                <StanButton
                                                    name='automation-add-link-button'
                                                    size='xs'
                                                    color='secondary'
                                                    onClick={() => setIsLinkModalOpen(true)}
                                                    icon={createIconElement(IcPlus, { className: 'size-4' })}
                                                    className='text-brand-main'
                                                >
                                                    <span className='max-sm:hidden'>Add a Link Button</span>
                                                </StanButton>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <StanTextArea
                                        id='content'
                                        {...field}
                                        placeholder="Hey! Thanks for reaching out. Here's what you asked for..."
                                        rows={6}
                                        className='bg-canvas min-h-24 w-full resize-none rounded-md'
                                    />
                                )}

                                <div className='mt-1 flex justify-end'>
                                    <span
                                        className={cn('text-caption-2-regular! text-gray', {
                                            'text-negative': isOverLimit,
                                        })}
                                    >
                                        {charCount} / {MAX_CHARS} characters
                                    </span>
                                </div>

                                {error && <p className='text-caption-2-bold text-negative md:-mt-3'>{error.message}</p>}
                                {buttonTitleError && (
                                    <p className='text-caption-2-bold text-negative md:-mt-3'>{buttonTitleError}</p>
                                )}
                            </div>
                        </div>
                    );
                }}
            />

            {/* Link Button Modal - Only ONE button is supported */}
            {hasAutoDMButtons && (
                <Controller
                    name='buttons'
                    control={control}
                    render={({ field }) => (
                        <LinkButtonModal
                            isOpen={isLinkModalOpen}
                            onClose={() => setIsLinkModalOpen(false)}
                            currentButton={linkButton}
                            onSave={buttonData => {
                                field.onChange([buttonData]);
                            }}
                            onRemove={() => {
                                field.onChange([]);
                            }}
                        />
                    )}
                />
            )}
        </>
    );
};
